import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";

export default function JoinPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/team/join/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Invitation not found or expired."));
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const r = await api.post(`/team/join/${token}/accept`);
      setAccepted(true);
      toast.success(`Joined ${r.data.workspace_owner_email}'s workspace.`);
      setTimeout(() => navigate("/app/projects"), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept invitation.");
    } finally { setAccepting(false); }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F8F9FA]" data-testid="join-page">
      <div className="card-crisp p-10 max-w-md text-center">
        <AlertCircle className="h-8 w-8 mx-auto text-red-500 mb-3" />
        <div className="text-lg font-display font-semibold text-[#0A0D14]">Invitation unavailable</div>
        <div className="mt-2 text-sm text-[#525B6A]">{error}</div>
      </div>
    </div>
  );
  if (!invite) return <div className="p-10 text-[#8A94A6]">Loading invitation…</div>;

  const emailMismatch = user && user.email && user.email.toLowerCase() !== invite.email.toLowerCase();

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F8F9FA]" data-testid="join-page">
      <div className="card-crisp p-10 max-w-md w-full">
        <Logo />
        {accepted ? (
          <div className="mt-8 text-center">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-600 mb-4" />
            <div className="text-lg font-display font-semibold text-[#0A0D14]">You&apos;re in.</div>
            <div className="mt-2 text-sm text-[#525B6A]">Redirecting to projects…</div>
          </div>
        ) : (
          <>
            <div className="mt-6 text-xs text-[#0047FF] uppercase tracking-wider font-semibold">Team invitation</div>
            <h1 className="mt-2 text-2xl font-display font-bold text-[#0A0D14]">
              {invite.workspace_owner_name} invited you.
            </h1>
            <p className="mt-3 text-sm text-[#525B6A]">
              You&apos;ve been invited to <strong>{invite.workspace_owner_email}</strong>&apos;s SerpNex workspace as a <span className="pill capitalize inline-flex">{invite.role}</span>.
            </p>
            <div className="mt-6 border-t border-[#E2E8F0] pt-6">
              <div className="text-xs text-[#8A94A6] mb-1 uppercase tracking-wider">Sent to</div>
              <div className="text-sm text-[#0A0D14] font-mono">{invite.email}</div>
            </div>

            {!user && (
              <div className="mt-6 space-y-2">
                <div className="text-sm text-[#525B6A]">To accept, sign in or create an account with <strong>{invite.email}</strong>.</div>
                <div className="flex gap-2">
                  <a href={`/signup?email=${encodeURIComponent(invite.email)}&next=${encodeURIComponent(`/join/${token}`)}`} data-testid="join-signup" className="btn-primary flex-1 text-center px-4 py-2.5 rounded-md text-sm">Create account</a>
                  <a href={`/login?next=${encodeURIComponent(`/join/${token}`)}`} data-testid="join-login" className="btn-ghost flex-1 text-center px-4 py-2.5 rounded-md text-sm">Sign in</a>
                </div>
              </div>
            )}

            {user && !emailMismatch && (
              <button onClick={accept} disabled={accepting} data-testid="join-accept" className="mt-6 btn-primary w-full px-4 py-2.5 rounded-md text-sm inline-flex items-center justify-center gap-2">
                {accepting ? "Joining…" : "Accept invitation"} <ArrowRight className="h-4 w-4" />
              </button>
            )}

            {user && emailMismatch && (
              <div className="mt-6 p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="join-mismatch">
                You&apos;re signed in as <strong>{user.email}</strong>, but this invitation was sent to <strong>{invite.email}</strong>. Log out and sign in with the invited email to accept.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
